import React, { useEffect, useState, useCallback } from "react";
import data from "../api/data";

const todayPlus = (days) => {
  const d = new Date();
  d.setDate(d.getDate() + days);
  return d.toISOString().slice(0, 10);
};

const formatDate = (iso) => {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleString();
  } catch (e) {
    return iso;
  }
};

const IssueCodeForm = ({ users, onIssued, busy, setBusy, setMessage }) => {
  const [userId, setUserId] = useState("");
  const [expiresAt, setExpiresAt] = useState(todayPlus(14));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!userId) {
      setMessage({ type: "error", text: "Pick a user." });
      return;
    }
    setBusy(true);
    try {
      const isoExpiry = new Date(expiresAt + "T23:59:59").toISOString();
      const res = await data.post("/admin/gamecodes/", {
        user_id: userId,
        expires_at: isoExpiry,
      });
      onIssued(res.data);
      setMessage({
        type: "success",
        text: "Issued code " + res.data.code + ".",
      });
      setUserId("");
    } catch (err) {
      const detail =
        (err.response && err.response.data && err.response.data.detail) ||
        "Failed to issue code.";
      setMessage({ type: "error", text: detail });
    } finally {
      setBusy(false);
    }
  };

  return (
    <form
      onSubmit={handleSubmit}
      className="flex flex-col sm:flex-row sm:items-end sm:space-x-4 space-y-3 sm:space-y-0 mb-6 bg-coolGray-50 p-4 rounded shadow"
    >
      <div className="flex flex-col">
        <label className="font-semibold mb-1 text-small">Assign to user</label>
        <select
          value={userId}
          onChange={(e) => setUserId(e.target.value)}
          className="border border-coolGray-300 rounded px-2 py-2 min-w-72"
        >
          <option value="">-- pick a user --</option>
          {users.map((u) => (
            <option key={u.id} value={u.id}>
              {u.full_name ? u.full_name + " — " + u.email : u.email}
            </option>
          ))}
        </select>
      </div>
      <div className="flex flex-col">
        <label className="font-semibold mb-1 text-small">Expires on</label>
        <input
          type="date"
          value={expiresAt}
          onChange={(e) => setExpiresAt(e.target.value)}
          min={todayPlus(1)}
          className="border border-coolGray-300 rounded px-2 py-2"
        />
      </div>
      <button
        type="submit"
        disabled={busy}
        className="py-2 px-5 text-white bg-dms-primary text-lg font-medium cursor-pointer transition duration-100 rounded hover:bg-blue-500 disabled:opacity-50"
      >
        {busy ? "Generating..." : "Generate"}
      </button>
    </form>
  );
};

const statusFor = (c) => {
  if (c.is_revoked) return { label: "revoked", color: "text-red-700" };
  if (c.is_expired) return { label: "expired", color: "text-coolGray-500" };
  const used = [];
  if (c.used_pandemic) used.push("pandemic");
  if (c.used_hostnations) used.push("hostnations");
  if (c.used_transcity) used.push("transcity");
  if (used.length === 3) return { label: "fully used", color: "text-amber-600" };
  if (used.length) return { label: "used in " + used.join(", "), color: "text-amber-600" };
  return { label: "active", color: "text-emerald-600" };
};

const GameCodesTable = ({ codes, onRevoke, busy }) => {
  if (!codes.length) {
    return <p className="text-coolGray-500">No codes issued yet.</p>;
  }
  return (
    <div className="overflow-x-auto">
      <table className="min-w-full border border-coolGray-200 text-sm">
        <thead className="bg-coolGray-100 text-left">
          <tr>
            <th className="p-2">Code</th>
            <th className="p-2">User</th>
            <th className="p-2">Expires</th>
            <th className="p-2">Days left</th>
            <th className="p-2">Status</th>
            <th className="p-2">Actions</th>
          </tr>
        </thead>
        <tbody>
          {codes.map((c) => {
            const s = statusFor(c);
            return (
              <tr key={c.id} className="border-t border-coolGray-200">
                <td className="p-2 font-mono">{c.code}</td>
                <td className="p-2">
                  {c.assigned_user_name || ""}
                  <br />
                  <span className="text-coolGray-500">
                    {c.assigned_user_email}
                  </span>
                </td>
                <td className="p-2">{formatDate(c.expires_at)}</td>
                <td className="p-2">{c.days_remaining}</td>
                <td className={"p-2 " + s.color}>{s.label}</td>
                <td className="p-2">
                  {!c.is_revoked && !c.is_expired && (
                    <button
                      onClick={() => onRevoke(c)}
                      disabled={busy}
                      className="text-red-600 hover:underline disabled:opacity-50"
                    >
                      Revoke
                    </button>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

const GameCodesAdmin = () => {
  const [users, setUsers] = useState([]);
  const [codes, setCodes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState(null);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [u, c] = await Promise.all([
        data.get("/admin/users/"),
        data.get("/admin/gamecodes/"),
      ]);
      setUsers(u.data);
      setCodes(c.data);
    } catch (err) {
      setMessage({ type: "error", text: "Failed to load." });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  const handleIssued = (code) => {
    setCodes((prev) => [code, ...prev]);
  };

  const handleRevoke = async (code) => {
    if (!window.confirm("Revoke code " + code.code + "? This cannot be undone.")) {
      return;
    }
    setBusy(true);
    try {
      const res = await data.delete("/admin/gamecodes/" + code.id + "/");
      setCodes((prev) => prev.map((c) => (c.id === code.id ? res.data : c)));
      setMessage({ type: "success", text: "Revoked " + code.code + "." });
    } catch (err) {
      setMessage({ type: "error", text: "Failed to revoke." });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="p-6 max-w-6xl">
      <h2 className="text-2xl font-semibold mb-4">Game Codes</h2>
      <p className="text-coolGray-600 mb-4">
        Issue time-bound codes to instructors. Each code allows the user to
        create <strong>one game in one</strong> of the three sims (Pandemic,
        Host Nations, or TransCity). Once claimed in any sim, the code cannot
        be used in another.
      </p>

      {message && (
        <div
          className={
            "mb-4 p-3 rounded " +
            (message.type === "error"
              ? "bg-red-100 text-red-700"
              : "bg-green-100 text-green-700")
          }
        >
          {message.text}
        </div>
      )}

      <IssueCodeForm
        users={users}
        onIssued={handleIssued}
        busy={busy}
        setBusy={setBusy}
        setMessage={setMessage}
      />

      {loading ? (
        <p>Loading...</p>
      ) : (
        <GameCodesTable codes={codes} onRevoke={handleRevoke} busy={busy} />
      )}
    </div>
  );
};

export default GameCodesAdmin;
